import { get } from 'lodash';

import { SaveInternalAdsInput } from '../../api/banners/BannersMutationExecutor';
import { mapInternalBannersSaveApiResponse } from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import {
    selectInternalBannerResourceRestrictions,
    selectInternalBannerResources
} from '../../selectors/internalBannerEdit/internalBannerEdit';
import { BannerId, SaveInternalBannersResult } from '../../typings/banners';
import { ProtocolValue } from '../../typings/bannersEditState';
import { ValidationResultItem } from '../../typings/common';
import {
    AddInternalAds,
    AddInternalAds_addedAds,
    DeleteAdsWithValidation,
    GdResourceType,
    GdResourceValueRestrictionType,
    InternalTemplateResourcesQuery_client_internalAdTemplateInfo_resources_valueRestrictions,
    UpdateInternalAds,
    UpdateInternalAds_updatedAds
} from '../../typings/graphql-grid';
import { InternalTemplateVars } from '../../typings/internalBanners';
import {
    EditedInternalBanner,
    EditInternalBannersState
} from '../../typings/internalBannersEditState';
import { UrlHelpers } from '../UrlHelpers';

import { strLengthForInternal } from './helpers';

export enum InternalBannerDefectCodes {
    REQUIRED_BUT_EMPTY = 'DefectIds.REQUIRED_BUT_EMPTY',
    MUST_BE_VALID_ID = 'DefectIds.MUST_BE_VALID_ID',
    CANNOT_BE_EMPTY = 'StringDefectIds.CANNOT_BE_EMPTY',
    INVALID_CHOICE_VALUE = 'DefectIds.INVALID_VALUE',
    MUST_BE_EMPTY = 'DefectIds.MUST_BE_EMPTY',
    LENGTH_CANNOT_BE_MORE_THAN_MAX = 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
    INTERNAL_TEMPLATE_NOT_ALLOWED = 'BannerDefectIds.Gen.INTERNAL_TEMPLATE_NOT_ALLOWED',
    TEXT_TOO_LONG = 'InternalAdRestrictionDefects.Length.TEXT_TOO_LONG',
    TEXT_TOO_SHORT = 'InternalAdRestrictionDefects.Length.TEXT_TOO_SHORT',
    URL_NOT_VALID = 'InternalAdRestrictionDefects.Url.URL_NOT_VALID',
    IMAGE_SIZE_KB_TOO_BIG = 'InternalAdRestrictionDefects.ImageSize.IMAGE_SIZE_KB_TOO_BIG',
    IMAGE_DIMENSION_TOO_BIG = 'InternalAdRestrictionDefects.ImageDimension.IMAGE_DIMENSION_TOO_BIG',
    IMAGE_DIMENSION_TOO_SMALL = 'InternalAdRestrictionDefects.ImageDimension.IMAGE_DIMENSION_TOO_SMALL',
    IMAGE_DIMENSION_NOT_EQUAL = 'InternalAdRestrictionDefects.ImageDimension.IMAGE_DIMENSION_NOT_EQUAL',
    IMAGE_FORMAT_INVALID = 'InternalAdRestrictionDefects.ImageFormat.IMAGE_FORMAT_INVALID'
}

const errorPath = (field: string, index: number, isNew: boolean): string =>
    isNew
        ? `adAddItems[${index}].${field}`
        : `adUpdateItems[${index}].${field}`;

export class InternalBannerValidator {
    static validateDescription(
        banner: EditedInternalBanner,
        index: number = 0
    ): ValidationResultItem[] {
        if (!banner.description) {
            return [
                {
                    code: InternalBannerDefectCodes.CANNOT_BE_EMPTY,
                    params: null,
                    path: errorPath(`description`, index, banner.isNew)
                }
            ];
        }

        const maxLength = 200;

        if (banner.description.trim().length > maxLength) {
            return [
                {
                    code:
                        InternalBannerDefectCodes.LENGTH_CANNOT_BE_MORE_THAN_MAX,
                    params: { maxLength },
                    path: errorPath(`description`, index, banner.isNew)
                }
            ];
        }

        return [];
    }

    protected static validateRequired(
        banner: EditedInternalBanner,
        state: EditInternalBannersState,
        index: number
    ): ValidationResultItem[] {
        const resourceRestrictions = selectInternalBannerResourceRestrictions(
            state,
            banner.campaign.id,
            banner.templateId
        );

        if (!resourceRestrictions) {
            return [];
        }

        return (
            resourceRestrictions
                .map(({ required, absent }) =>
                    banner.templateVars
                        .filter(
                            templateVar =>
                                banner.templateId === templateVar.templateId
                        )
                        .reduce((errors, { resourceId, value }, position) => {
                            if (required.includes(resourceId) && !value) {
                                return [
                                    ...errors,
                                    {
                                        code:
                                            InternalBannerDefectCodes.REQUIRED_BUT_EMPTY,
                                        params: null,
                                        path: errorPath(
                                            `templateVars[${position}]`,
                                            index,
                                            banner.isNew
                                        )
                                    }
                                ];
                            }

                            if (absent.includes(resourceId) && value) {
                                return [
                                    ...errors,
                                    {
                                        code:
                                            InternalBannerDefectCodes.MUST_BE_EMPTY,
                                        params: null,
                                        path: errorPath(
                                            `templateVars[${position}]`,
                                            index,
                                            banner.isNew
                                        )
                                    }
                                ];
                            }

                            return errors;
                        }, [])
                )
                .sort((a, b) => a.length - b.length)
                .shift() || []
        );
    }

    protected static validateTemplateId(
        banner: EditedInternalBanner,
        index: number
    ): ValidationResultItem[] {
        if (!(banner.templateId && Number(banner.templateId) > 0)) {
            return [
                {
                    code: InternalBannerDefectCodes.MUST_BE_VALID_ID,
                    params: null,
                    path: errorPath(`templateId`, index, banner.isNew)
                }
            ];
        }

        return [];
    }

    protected static validateTemplateVarValue(
        banner: EditedInternalBanner,
        value: string,
        type: GdResourceType,
        restriction: InternalTemplateResourcesQuery_client_internalAdTemplateInfo_resources_valueRestrictions,
        position: number,
        index: number
    ): ValidationResultItem[] {
        let isInvalid = false;
        let params = null;

        switch (restriction.type) {
            case GdResourceValueRestrictionType.TEXT_LENGTH_MAX: {
                const maxLength = get(restriction, 'length');
                const length =
                    type === GdResourceType.TEXT
                        ? strLengthForInternal(value)
                        : value.length;

                isInvalid = length > maxLength;
                params = { maxLength };

                break;
            }
            case GdResourceValueRestrictionType.TEXT_LENGTH_MIN: {
                const minLength = get(restriction, 'length');
                const length =
                    type === GdResourceType.TEXT
                        ? strLengthForInternal(value)
                        : value.length;

                isInvalid = length < minLength;
                params = { minLength };

                break;
            }
            case GdResourceValueRestrictionType.URL_CORRECT: {
                const isHttpsOnly = get(restriction, 'isHttpsOnly');

                isInvalid = !(
                    UrlHelpers.isUrl(value) &&
                    (isHttpsOnly
                        ? value.startsWith(ProtocolValue.https)
                        : value.startsWith(ProtocolValue.https) ||
                          value.startsWith(ProtocolValue.http))
                );

                params = { isHttpsOnly };

                break;
            }
            default:
                break;
        }

        if (isInvalid && restriction.strict) {
            return [
                {
                    code: restriction.defectCode,
                    params: params,
                    path: errorPath(
                        `templateVars[${position}]`,
                        index,
                        banner.isNew
                    )
                }
            ];
        }

        return [];
    }

    protected static validateTemplateVar(
        banner: EditedInternalBanner,
        templateVar: InternalTemplateVars,
        state: EditInternalBannersState,
        position: number,
        index: number
    ): ValidationResultItem[] {
        const { resourceId, value } = templateVar;

        if (!value) {
            return [];
        }

        const resources = selectInternalBannerResources(
            state,
            banner.campaign.id,
            banner.templateId
        );

        const resource =
            resources && resources.find(item => item.id === resourceId);

        if (!resource) {
            return [];
        }

        if (
            resource.choices &&
            !resource.choices.find(v => v.value === value)
        ) {
            return [
                {
                    code: InternalBannerDefectCodes.INVALID_CHOICE_VALUE,
                    params: null,
                    path: errorPath(
                        `templateVars[${position}]`,
                        index,
                        banner.isNew
                    )
                }
            ];
        }

        return resource.restrictions.reduce((resultItems, restriction) => {
            return [
                ...resultItems,
                ...InternalBannerValidator.validateTemplateVarValue(
                    banner,
                    value,
                    resource.type,
                    restriction,
                    position,
                    index
                )
            ];
        }, []);
    }

    protected static validateBanners(
        banners: Record<BannerId, EditedInternalBanner>,
        state: EditInternalBannersState
    ): ValidationResultItem[] {
        const bannersIds: BannerId[] = Object.keys(banners);

        return bannersIds.reduce(
            (errors: ValidationResultItem[], id: BannerId, index: number) => {
                return [
                    ...errors,
                    ...InternalBannerValidator.validate(
                        banners[id],
                        state,
                        index
                    )
                ];
            },
            []
        );
    }

    static findErrors(
        input: SaveInternalAdsInput,
        state: EditInternalBannersState
    ): SaveInternalBannersResult | null {
        const validationResultUpdateErrors = InternalBannerValidator.validateBanners(
            input.update,
            state
        );
        const validationResultAddErrors = InternalBannerValidator.validateBanners(
            input.add,
            state
        );

        if (
            validationResultUpdateErrors.length === 0 &&
            validationResultAddErrors.length === 0
        ) {
            return null;
        }

        return mapInternalBannersSaveApiResponse(
            {
                updatedAds: [] as UpdateInternalAds_updatedAds[],
                validationResult: {
                    errors: validationResultUpdateErrors
                }
            } as UpdateInternalAds,
            {
                addedAds: [] as AddInternalAds_addedAds[],
                validationResult: {
                    errors: validationResultAddErrors
                }
            } as AddInternalAds,
            {
                validationResult: null
            } as DeleteAdsWithValidation,
            input
        );
    }

    static validate(
        banner: EditedInternalBanner,
        state: EditInternalBannersState,
        index: number = 0
    ): ValidationResultItem[] {
        return [
            ...InternalBannerValidator.validateDescription(banner, index),
            ...InternalBannerValidator.validateTemplateId(banner, index),
            ...InternalBannerValidator.validateRequired(banner, state, index),
            ...banner.templateVars
                .filter(
                    templateVar => banner.templateId === templateVar.templateId
                )
                .reduce(
                    (errors, templateVar, position) => [
                        ...errors,
                        ...InternalBannerValidator.validateTemplateVar(
                            banner,
                            templateVar,
                            state,
                            position,
                            index
                        )
                    ],
                    []
                )
        ];
    }
}
